import DisclosureNotice from '../../../components/ai/DisclosureNotice';
import ManuscriptDisclosureEditor from '../../../components/ai/ManuscriptDisclosureEditor';
import React, {useState, useEffect, useCallback} from 'react';
import moment from 'moment';

// redux
import {useDispatch, useSelector} from 'react-redux';

import styled from 'styled-components';

// pop-ups
import {toast} from 'react-toastify';

import {Link, useHistory} from 'react-router-dom';
import {Alert, Button, Dialog} from '@betareader/design-system';
import AppIcon from '../../../components/AppIcon';
import {withTranslation, Trans} from 'react-i18next';

import language from '../../../config/language';
import genreList from '../../../shared/constants/genres';
import {authUtil, accessUtil} from '../../../utils';
import {
  deleteBook,
  saveBook,
  setSaveButton
} from '../../../modules/book';

import PublishingSettings from './publishingSettings';
import BookNotificationSettings from '../settings/bookNotificationSettings';
import {PaddedWrapper} from '../../../style';
import './editBookDetails.css';

const Horizontal = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const Vertical = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 2px;
`;

const fieldValue = handler => event => handler(event, {value: event.target.value});
const checkedValue = handler => event => handler(event, {checked: event.target.checked});
const fieldsToCompare = [
  'title',
  'genres',
  'description',
  'feedbackGuidelines',
  'copyRightInfo',
  'language',
  'customAuthorName',
  'status',
  'isMature',
  'pubDate',
  'enableChapterComments',
  'manuscriptStyle'
];

export const EditBookDetails = ({t}) => {
  const [pendingBookChanges, setPendingBookChanges] = useState({});
  const [coverUploadActive, setCoverUploadActive] = useState(false);
  const [showDeleteBookModal, setShowDeleteBookModal] = useState(false);

  const currentBook = useSelector(state => state.book.currentBook);

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    let hasPendingChanges = false;
    fieldsToCompare.forEach(field => {
      if (
        pendingBookChanges[field] !== undefined &&
        pendingBookChanges[field] !== currentBook[field]
      ) {
        hasPendingChanges = true;
      }
    });
    if (hasPendingChanges) {
      enableSaveButton();
    }
  }, [currentBook, pendingBookChanges]);

  const genreOptions = useCallback(() => {
    return genreList.map(genre => {
      return {key: genre, value: genre, text: genre};
    });
  }, [genreList]);

  const availableLanguages = useCallback(() => {
    return language.getAvailableLanguages().map(languageOption => ({
      key: languageOption.code,
      value: languageOption.code,
      text: languageOption.name
    }));
  }, [genreList]);

  const canEdit = useCallback(() => {
    return accessUtil.isAllowed(currentBook.access, ['edit']);
  }, [currentBook?.access]);

  const getFieldValue = field => {
    return pendingBookChanges[field] ?? currentBook[field];
  };

  const titleChanged = (event, data) => {
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      title: data.value
    });
  };

  const genresChanged = (event, data) => {
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      genres: data.value
    });
  };

  const descriptionChanged = (event, data) => {
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      description: data.value
    });
  };

  const feedbackGuidelinesChanged = (event, data) => {
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      feedbackGuidelines: data.value
    });
  };

  const copyRightInfoChanged = (event, data) => {
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      copyRightInfo: data.value
    });
  };

  const languageChanged = (event, data) => {
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      language: data.value
    });
  };

  const customAuthorNameChanged = (event, data) => {
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      customAuthorName: data.value
    });
  };

  const pubDateChanged = (event, data) => {
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      pubDate: data.value
    });
  };

  const toggleEnableChapterComments = (event, data) => {
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      enableChapterComments: data.checked
    });
  };

  const handleStatusChange = (event, data) => {
    console.log(data);
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      status: data.value
    });
  };

  const handleStyleChange = (event, data) => {
    console.log(data);
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      manuscriptStyle: data.value
    });
  };

  const toggleIsMature = (event, data) => {
    if (!canEdit()) {
      return;
    }
    updatePendingBookChanges({
      isMature: data.checked
    });
  };

  const updatePendingBookChanges = data => {
    setPendingBookChanges({
      ...pendingBookChanges,
      ...data
    });
  };

  const enableSaveButton = (props = {}) => {
    dispatch(
      setSaveButton(
        <Button
          onClick={savePendingChanges}
          {...props}
        >{t('Save')}</Button>
      )
    );
  };

  const savePendingChanges = async () => {
    if (!canEdit()) {
      return;
    }
    enableSaveButton({disabled: true, 'aria-busy': true});
    const idToken = await authUtil.getFreshIdToken();
    dispatch(
      saveBook(idToken, {
        ...pendingBookChanges,
        _id: currentBook._id
      })
    );
  };

  const handleDeleteBook = async () => {
    if (!canEdit()) {
      return;
    }
    const idToken = await authUtil.getFreshIdToken();
    dispatch(
      deleteBook(idToken, currentBook, () => {
        toast.info(
          <span>
            <AppIcon name='trash' aria-hidden='true' />
            {t('DeletedBookX', {x: currentBook.title})}
          </span>
        );
        history.push('/mymanuscripts');
      })
    );
  };

  const selectedGenres = getFieldValue('genres') || [];
  const editable = canEdit();

  return (
    <PaddedWrapper>
      <div className="br-book-edit">
        <header>
          <h1>
            <input
              className="br-book-edit__title"
              type="text"
              maxLength={60}
              aria-label={t('Title')}
              placeholder={t('Title')}
              value={getFieldValue('title') || ''}
              disabled={!editable}
              onChange={fieldValue(titleChanged)}
            />
          </h1>
          <p>{`${t('by')} ${currentBook.authorName}`}</p>
          {currentBook.privacy !== 'unpublished' && currentBook.wordCount === 0 && (
            <Alert variant="warning">
              <Trans i18nKey="BookHas0WordsDescription">
                <span>This book has at least one version with 0 words. You can add or upload chapters </span>
                <Link to={`/books/${currentBook._id}/content`}>here</Link>.
              </Trans>
            </Alert>
          )}
        </header>
        {editable && <DisclosureNotice key={`notice-${currentBook._id}`} book={currentBook} />}
        {editable && <ManuscriptDisclosureEditor key={currentBook._id} book={currentBook} />}
        <div className="br-book-edit__section">
          <label>{t('PublishingStatus')}</label>
          <PublishingSettings currentBook={currentBook} />
          <Horizontal>
            <Vertical>
              <label htmlFor="book-status">{t('ManuscriptStatus')}</label>
              <select id="book-status" className="br-field__input" disabled={!editable}
                value={getFieldValue('status') || ''} onChange={fieldValue(handleStatusChange)}>
                <option value="" disabled>{t('SelectStatus')}</option>
                <option value="alpha">alpha — {t('MSStatusDescription_alpha')}</option>
                <option value="beta">beta — {t('MSStatusDescription_beta')}</option>
              </select>
            </Vertical>
            <Vertical>
              <label htmlFor="book-style">{t('ManuscriptStyle')}</label>
              <select id="book-style" className="br-field__input" disabled={!editable}
                value={getFieldValue('manuscriptStyle') || ''} onChange={fieldValue(handleStyleChange)}>
                <option value="" disabled>{t('SelectManuscriptStyle')}</option>
                <option value="book">{t('Book')}</option>
                <option value="screenplay">{t('Screenplay')}</option>
              </select>
            </Vertical>
          </Horizontal>
        </div>
        <details className="br-book-edit__genres">
          <summary>{selectedGenres.length > 5 ? t('SelectUpTo5Genres') : t('Genres')}: {selectedGenres.join(', ') || t('SelectUpTo5Genres')}</summary>
          <fieldset disabled={!editable}>
            <legend>{t('SelectUpTo5Genres')}</legend>
            <div className="br-book-edit__genre-list">
              {genreOptions().map(genre => {
                const selected = selectedGenres.includes(genre.value);
                return <label key={genre.key}>
                  <input type="checkbox" value={genre.value} checked={selected}
                    disabled={!selected && selectedGenres.length >= 5}
                    onChange={() => genresChanged(null, {value: selected
                      ? selectedGenres.filter(value => value !== genre.value)
                      : [...selectedGenres, genre.value]})} />
                  {genre.text}
                </label>;
              })}
            </div>
          </fieldset>
        </details>
        <div className="br-book-edit__fields">
          <label htmlFor="book-description">{t('Description')}</label>
          <textarea id="book-description" className="br-field__input book-description" rows={8} maxLength={1000}
            disabled={!editable} placeholder={t('WhatIsThisStoryAbout')}
            value={getFieldValue('description') || ''} onChange={fieldValue(descriptionChanged)} />
          <label htmlFor="book-feedback-guidelines">{t('FeedbackGuidelines')}</label>
          <textarea id="book-feedback-guidelines" className="br-field__input book-feedback-guidelines" maxLength={1000}
            disabled={!editable} placeholder={t('WhatDoYouNeedHelpWith')}
            value={getFieldValue('feedbackGuidelines') || ''} onChange={fieldValue(feedbackGuidelinesChanged)} />
          <label htmlFor="book-copyright">{t('CopyrightInfo')}</label>
          <textarea id="book-copyright" className="br-field__input book-copyright" maxLength={1000}
            disabled={!editable} placeholder={`${t('CopyrightInfo')}...`}
            value={getFieldValue('copyRightInfo') || ''} onChange={fieldValue(copyRightInfoChanged)} />
          <label htmlFor="book-language">{t('Language')}</label>
          <select id="book-language" className="br-field__input book-language" disabled={!editable}
            value={getFieldValue('language') || ''} onChange={fieldValue(languageChanged)}>
            <option value="">{t('Language')}</option>
            {availableLanguages().map(option => <option key={option.key} value={option.value}>{option.text}</option>)}
          </select>
          <label htmlFor="book-pen-name">{t('PenName')}</label>
          <input id="book-pen-name" className="br-field__input" type="text" maxLength={50}
            disabled={!editable} value={getFieldValue('customAuthorName') || ''}
            onChange={fieldValue(customAuthorNameChanged)}
            placeholder={t('DefaultNamePlaceholder', {displayName: currentBook.author.displayName})} />
          <label className="br-book-edit__checkbox">
            <input type="checkbox" disabled={!editable} checked={Boolean(getFieldValue('isMature'))}
              onChange={checkedValue(toggleIsMature)} />{t('MatureContent')}
          </label>
          <label htmlFor="book-publication-date">{t('PublicationDate')}</label>
          <input id="book-publication-date" className="br-field__input" type="date" disabled={!editable}
            value={getFieldValue('pubDate') ? moment(getFieldValue('pubDate')).format('YYYY-MM-DD') : ''}
            onChange={fieldValue(pubDateChanged)} />
          <fieldset>
            <legend>{t('Feedback')}</legend>
            <label className="br-book-edit__checkbox">
              <input type="checkbox" disabled={!editable}
                checked={Boolean(getFieldValue('enableChapterComments'))}
                onChange={checkedValue(toggleEnableChapterComments)} />{t('EnableComments')}
            </label>
          </fieldset>
          <BookNotificationSettings />
        </div>
        {editable && (
          <Button variant="danger" onClick={() => setShowDeleteBookModal(true)}>
            <AppIcon name="trash" aria-hidden="true" /> {t('DeleteBook')}
          </Button>
        )}
        <Dialog open={showDeleteBookModal} title={t('DeleteBook')}
          onClose={() => setShowDeleteBookModal(false)}
          actions={<>
            <Button variant="secondary" onClick={() => setShowDeleteBookModal(false)}>{t('No')}</Button>
            <Button variant="danger" onClick={handleDeleteBook}>{t('Yes')}</Button>
          </>}>
          <p>{t('AreYouSureYouWantToDeleteBookName', {bookTitle: currentBook.title})}</p>
        </Dialog>
      </div>
    </PaddedWrapper>
  );
};

export default withTranslation()(EditBookDetails);
